import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddActivitiesOptions,
  AddActivitiesResponse,
  AddActivityResponse,
  AggregateOrdersOptions,
  AggregateOrdersResponse,
  BulkDeleteImportedOrdersResponse,
  BulkMarkOrdersAsPaidResponse,
  BulkUpdateOrderTagsOptions,
  BulkUpdateOrderTagsResponse,
  BulkUpdateOrdersOptions,
  BulkUpdateOrdersResponse,
  CancelOrderApplicationErrors,
  CancelOrderOptions,
  CancelOrderResponse,
  CaptureAuthorizedPaymentsApplicationErrors,
  CaptureAuthorizedPaymentsResponse,
  ChargeMembershipsOptions,
  CreateOrderApplicationErrors,
  CreateOrderOptions,
  CreatePaymentGatewayOrderResponse,
  DeleteActivityIdentifiers,
  DeleteActivityResponse,
  GetOrderApplicationErrors,
  GetPaymentCollectabilityStatusResponse,
  GetRefundabilityStatusResponse,
  ImportOrderResponse,
  MarkOrderAsPaidResponse,
  MaskedOrder,
  Order,
  OrderApprovedEnvelope,
  OrderCanceledEnvelope,
  OrderCommittedEnvelope,
  OrderCreatedEnvelope,
  OrderFulfilledEnvelope,
  OrderPaymentStatusUpdatedEnvelope,
  OrderSearch,
  OrderStatusWithLiterals,
  OrderUpdatedEnvelope,
  PaymentCapture,
  PaymentCollectionCreatePaymentGatewayOrderApplicationErrors,
  PaymentCollectionCreatePaymentGatewayOrderOptions,
  PaymentCollectionMarkOrderAsPaidApplicationErrors,
  PaymentCollectionMarkOrderAsPaidOptions,
  PaymentRefund,
  PreparePaymentCollectionApplicationErrors,
  PreparePaymentCollectionOptions,
  PreparePaymentCollectionResponse,
  Price,
  PublicActivity,
  RecordManuallyCollectedPaymentApplicationErrors,
  RecordManuallyCollectedPaymentOptions,
  SearchOrdersResponse,
  SetOrderNumberCounterOptions,
  SetOrderNumberCounterResponse,
  TriggerRefundApplicationErrors,
  TriggerRefundOptions,
  TriggerRefundResponse,
  UpdateActivityIdentifiers,
  UpdateActivityResponse,
  UpdateOrder,
  UpdateOrderApplicationErrors,
  UpdateOrderLineItem,
  UpdateOrderLineItemIdentifiers,
  UpdateOrderLineItemResponse,
  UpdateOrderStatusApplicationErrors,
  UpdateOrderStatusResponse,
  VoidAuthorizedPaymentsApplicationErrors,
  VoidAuthorizedPaymentsResponse,
  addActivities as universalAddActivities,
  addActivity as universalAddActivity,
  aggregateOrders as universalAggregateOrders,
  bulkDeleteImportedOrders as universalBulkDeleteImportedOrders,
  bulkUpdateOrderTags as universalBulkUpdateOrderTags,
  bulkUpdateOrders as universalBulkUpdateOrders,
  cancelOrder as universalCancelOrder,
  captureAuthorizedPayments as universalCaptureAuthorizedPayments,
  chargeMemberships as universalChargeMemberships,
  createOrder as universalCreateOrder,
  deleteActivity as universalDeleteActivity,
  getOrder as universalGetOrder,
  getPaymentCollectabilityStatus as universalGetPaymentCollectabilityStatus,
  getRefundabilityStatus as universalGetRefundabilityStatus,
  importOrder as universalImportOrder,
  paymentCollectionBulkMarkOrdersAsPaid as universalPaymentCollectionBulkMarkOrdersAsPaid,
  paymentCollectionCreatePaymentGatewayOrder as universalPaymentCollectionCreatePaymentGatewayOrder,
  paymentCollectionMarkOrderAsPaid as universalPaymentCollectionMarkOrderAsPaid,
  preparePaymentCollection as universalPreparePaymentCollection,
  recordManuallyCollectedPayment as universalRecordManuallyCollectedPayment,
  searchOrders as universalSearchOrders,
  setOrderNumberCounter as universalSetOrderNumberCounter,
  triggerRefund as universalTriggerRefund,
  updateActivity as universalUpdateActivity,
  updateOrder as universalUpdateOrder,
  updateOrderLineItem as universalUpdateOrderLineItem,
  updateOrderStatus as universalUpdateOrderStatus,
  voidAuthorizedPayments as universalVoidAuthorizedPayments,
} from './ecom-v1-order-orders.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

/** @internal */
export function importOrder(httpClient: HttpClient): ImportOrderSignature {
  return (
    order: NonNullablePaths<
      Order,
      | `billingInfo.contactDetails`
      | `channelInfo`
      | `currencyConversionDetails.conversionRate`
      | `currencyConversionDetails.originalCurrency`
      | `fulfillmentStatus`
      | `lineItems`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.itemType`
      | `lineItems.${number}.price`
      | `lineItems.${number}.productName`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.quantity`
      | `paymentStatus`
      | `priceSummary`
      | `status`,
      5
    >
  ) =>
    universalImportOrder(
      order,
      // @ts-ignore
      { httpClient }
    );
}

interface ImportOrderSignature {
  /**
   * Imports a single order with historical data while preserving original details and controlling side effects.
   * Can be used for initial import or update (delete-insert).
   * Only imported via ImportOrder API orders can be updated, Order.id used as key for update.
   * Order will be fully overridden.
   * @param - Order to be imported
   * If order.order_settings.order_type and order.order_settings.custom_order_type not set than VIEW_ONLY will be used
   */
  (
    order: NonNullablePaths<
      Order,
      | `billingInfo.contactDetails`
      | `channelInfo`
      | `currencyConversionDetails.conversionRate`
      | `currencyConversionDetails.originalCurrency`
      | `fulfillmentStatus`
      | `lineItems`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.itemType`
      | `lineItems.${number}.price`
      | `lineItems.${number}.productName`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.quantity`
      | `paymentStatus`
      | `priceSummary`
      | `status`,
      5
    >
  ): Promise<
    NonNullablePaths<
      ImportOrderResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    >
  >;
}

/** @internal */
export function setOrderNumberCounter(
  httpClient: HttpClient
): SetOrderNumberCounterSignature {
  return (counter: string, options?: SetOrderNumberCounterOptions) =>
    universalSetOrderNumberCounter(
      counter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SetOrderNumberCounterSignature {
  /**
   * Sets the current order increment ID for a site. This defines the starting number
   * for subsequent new orders created after migration.
   * @param - Order number counter to set.
   * Counter will be used as start of number sequence and used as order number with increment
   */
  (counter: string, options?: SetOrderNumberCounterOptions): Promise<
    NonNullablePaths<
      SetOrderNumberCounterResponse,
      `oldCounter` | `newCounter`,
      2
    >
  >;
}

/** @internal */
export function bulkDeleteImportedOrders(
  httpClient: HttpClient
): BulkDeleteImportedOrdersSignature {
  return (orderIds: string[]) =>
    universalBulkDeleteImportedOrders(
      orderIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteImportedOrdersSignature {
  /**
   * Bulk deletes previously imported orders. Only works on orders
   * that were created through the import process.
   * @param - List of order ids to be deleted.
   * Only orders imported via ImportOrders API can be deleted.
   */
  (orderIds: string[]): Promise<
    NonNullablePaths<BulkDeleteImportedOrdersResponse, `jobId`, 2>
  >;
}

export function preparePaymentCollection(
  httpClient: HttpClient
): PreparePaymentCollectionSignature {
  return (
    ecomOrderId: string,
    amount: Price,
    options?: PreparePaymentCollectionOptions
  ) =>
    universalPreparePaymentCollection(
      ecomOrderId,
      amount,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PreparePaymentCollectionSignature {
  /**
   * Prepares payment collection for given ecom order. This is the first of 2-step process of payment collection.
   * Here we ensure that payment collection is possible for given order and store and prepare payment gateway order for future charge.
   * 2nd step is an actual charge of prepared payment gateway order. This could be done either
   * via Wix-Cashier's API (https://bo.wix.com/wix-docs/rest/wix-cashier/pay/charge/charge-for-order)
   * or using Cashier Payments Widget (https://github.com/wix-private/cashier-client/tree/master/packages/cashier-payments-widget)
   * @param - Ecom order ID.
   * @param - Amount to collect
   */
  (
    ecomOrderId: string,
    amount: Price,
    options?: PreparePaymentCollectionOptions
  ): Promise<
    NonNullablePaths<
      PreparePaymentCollectionResponse,
      `paymentGatewayOrderId`,
      2
    > & {
      __applicationErrorsType?: PreparePaymentCollectionApplicationErrors;
    }
  >;
}

export function getPaymentCollectabilityStatus(
  httpClient: HttpClient
): GetPaymentCollectabilityStatusSignature {
  return (ecomOrderId: string) =>
    universalGetPaymentCollectabilityStatus(
      ecomOrderId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPaymentCollectabilityStatusSignature {
  /**
   * Provides payment collectability status for given order. If payment collection is possible
   * response will contain collectable amount for given ecom order. If not - response will contain
   * reason why payment collection is not possible.
   * @param - Ecom order ID.
   */
  (ecomOrderId: string): Promise<
    NonNullablePaths<
      GetPaymentCollectabilityStatusResponse,
      `status` | `amount.amount` | `amount.formattedAmount`,
      3
    >
  >;
}

/** @internal */
export function recordManuallyCollectedPayment(
  httpClient: HttpClient
): RecordManuallyCollectedPaymentSignature {
  return (
    orderId: string,
    amount: Price,
    options?: RecordManuallyCollectedPaymentOptions
  ) =>
    universalRecordManuallyCollectedPayment(
      orderId,
      amount,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RecordManuallyCollectedPaymentSignature {
  /**
   * Records and approves new manual payment with provided custom amount on given order.
   * Existing pending payments are ignored.
   * Custom amount is expected to be less or equal remaining amount to be paid on order (affected by approved payments, refunds and gift card payments)
   * @param - Order ID.
   * @param - Amount to be recorded as approved manual payment for given order
   */
  (
    orderId: string,
    amount: Price,
    options?: RecordManuallyCollectedPaymentOptions
  ): Promise<
    void & {
      __applicationErrorsType?: RecordManuallyCollectedPaymentApplicationErrors;
    }
  >;
}

/** @internal */
export function paymentCollectionMarkOrderAsPaid(
  httpClient: HttpClient
): PaymentCollectionMarkOrderAsPaidSignature {
  return (
    ecomOrderId: string,
    options?: PaymentCollectionMarkOrderAsPaidOptions
  ) =>
    universalPaymentCollectionMarkOrderAsPaid(
      ecomOrderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PaymentCollectionMarkOrderAsPaidSignature {
  /**
   * Marks the order as paid. `order.paymentStatus` field *eventually* changes to `PAID`.
   * In case the order already has an offline payment transaction associated with it
   * (usually when manual payment method is chosen at checkout) - This transaction will become approved.
   * In case the order has no payment transactions associated with it
   * (usually when the item is set to be paid offline after checkout or when an order is created from the backoffice) - A payment transaction
   * will be created and approved.
   * @param - Ecom order ID.
   */
  (
    ecomOrderId: string,
    options?: PaymentCollectionMarkOrderAsPaidOptions
  ): Promise<
    NonNullablePaths<
      MarkOrderAsPaidResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    > & {
      __applicationErrorsType?: PaymentCollectionMarkOrderAsPaidApplicationErrors;
    }
  >;
}

/** @internal */
export function paymentCollectionBulkMarkOrdersAsPaid(
  httpClient: HttpClient
): PaymentCollectionBulkMarkOrdersAsPaidSignature {
  return (ecomOrderIds: string[]) =>
    universalPaymentCollectionBulkMarkOrdersAsPaid(
      ecomOrderIds,
      // @ts-ignore
      { httpClient }
    );
}

interface PaymentCollectionBulkMarkOrdersAsPaidSignature {
  /**
   * Marks multiple orders as paid. `order.paymentStatus` field *eventually* changes to `PAID`.
   * @param - IDs of orders to mark as paid.
   */
  (ecomOrderIds: string[]): Promise<
    NonNullablePaths<
      BulkMarkOrdersAsPaidResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.number`
      | `results.${number}.item.buyerInfo.visitorId`
      | `results.${number}.item.buyerInfo.memberId`
      | `results.${number}.item.paymentStatus`
      | `results.${number}.item.fulfillmentStatus`
      | `results.${number}.item.weightUnit`
      | `results.${number}.item.taxIncludedInPrices`
      | `results.${number}.item.shippingInfo.title`
      | `results.${number}.item.status`
      | `results.${number}.item.attributionSource`
      | `results.${number}.item.createdBy.userId`
      | `results.${number}.item.createdBy.memberId`
      | `results.${number}.item.createdBy.visitorId`
      | `results.${number}.item.createdBy.appId`
      | `results.${number}.item.channelInfo.type`
      | `results.${number}.item.businessLocation._id`
      | `results.${number}.item.businessLocation.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

/** @internal */
export function getRefundabilityStatus(
  httpClient: HttpClient
): GetRefundabilityStatusSignature {
  return (ecomOrderId: string) =>
    universalGetRefundabilityStatus(
      ecomOrderId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetRefundabilityStatusSignature {
  /**
   * Checks whether this order can be refunded.
   * @param - Order ID.
   */
  (ecomOrderId: string): Promise<
    NonNullablePaths<
      GetRefundabilityStatusResponse,
      | `refundabilities`
      | `refundabilities.${number}.nonRefundableReason`
      | `refundabilities.${number}.manuallyRefundableReason`
      | `refundabilities.${number}.paymentId`
      | `refundabilities.${number}.refundabilityStatus`
      | `refundablePerItem`,
      4
    >
  >;
}

/** @internal */
export function paymentCollectionCreatePaymentGatewayOrder(
  httpClient: HttpClient
): PaymentCollectionCreatePaymentGatewayOrderSignature {
  return (
    ecomOrderId: string,
    options?: PaymentCollectionCreatePaymentGatewayOrderOptions
  ) =>
    universalPaymentCollectionCreatePaymentGatewayOrder(
      ecomOrderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PaymentCollectionCreatePaymentGatewayOrderSignature {
  /**
   * Call this endpoint to create an order in the payment gateway system. The amount of the order would be either:
   * 1. An explicit amount provided in the request, or;
   * 2. If an explicit amount is not provided - the remaining amount to complete the payment of that eCom order.
   * As a result, an ID of the created payment gateway order would be returned.
   * You can then use Wix Payments APIs to approve that order or collect payment, which will eventually change the eCom order state (e.g mark it as paid).
   * @param - Ecom order ID.
   */
  (
    ecomOrderId: string,
    options?: PaymentCollectionCreatePaymentGatewayOrderOptions
  ): Promise<
    NonNullablePaths<
      CreatePaymentGatewayOrderResponse,
      `paymentGatewayOrderId`,
      2
    > & {
      __applicationErrorsType?: PaymentCollectionCreatePaymentGatewayOrderApplicationErrors;
    }
  >;
}

/** @internal */
export function chargeMemberships(
  httpClient: HttpClient
): ChargeMembershipsSignature {
  return (
    ecomOrderId: string,
    memberId: string,
    options?: NonNullablePaths<
      ChargeMembershipsOptions,
      | `membershipCharges.${number}.appId`
      | `membershipCharges.${number}.catalogReference`
      | `membershipCharges.${number}.catalogReference.appId`
      | `membershipCharges.${number}.catalogReference.catalogItemId`
      | `membershipCharges.${number}.membershipId`
      | `membershipCharges.${number}.membershipName`
      | `membershipCharges.${number}.membershipName.original`
      | `membershipCharges.${number}.rootCatalogItemId`,
      5
    >
  ) =>
    universalChargeMemberships(
      ecomOrderId,
      memberId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ChargeMembershipsSignature {
  /**
   * @param - Order ID.
   * @param - The member id. Do not attempt to get it from the request context, since in some cases the caller is not a member
   * but a user which is using the membership on behalf of the a member
   */
  (
    ecomOrderId: string,
    memberId: string,
    options?: NonNullablePaths<
      ChargeMembershipsOptions,
      | `membershipCharges.${number}.appId`
      | `membershipCharges.${number}.catalogReference`
      | `membershipCharges.${number}.catalogReference.appId`
      | `membershipCharges.${number}.catalogReference.catalogItemId`
      | `membershipCharges.${number}.membershipId`
      | `membershipCharges.${number}.membershipName`
      | `membershipCharges.${number}.membershipName.original`
      | `membershipCharges.${number}.rootCatalogItemId`,
      5
    >
  ): Promise<void>;
}

/** @internal */
export function triggerRefund(httpClient: HttpClient): TriggerRefundSignature {
  return (
    ecomOrderId: string,
    payments: NonNullablePaths<PaymentRefund, `paymentId`, 2>[],
    options?: TriggerRefundOptions
  ) =>
    universalTriggerRefund(
      ecomOrderId,
      payments,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TriggerRefundSignature {
  /**
   * Calls corresponding payment providers and creates refund transactions for requested payments.
   * Updates order transactions based on refund results.
   * For requested payments with `TriggerRefundRequest.payments.external_refund = true` will not call payment providers and
   * will only update order transactions.
   * @param - The order this refund related to
   * @param - Refund operations information
   */
  (
    ecomOrderId: string,
    payments: NonNullablePaths<PaymentRefund, `paymentId`, 2>[],
    options?: TriggerRefundOptions
  ): Promise<
    NonNullablePaths<
      TriggerRefundResponse,
      | `orderTransactions.orderId`
      | `orderTransactions.payments`
      | `orderTransactions.payments.${number}.regularPaymentDetails.offlinePayment`
      | `orderTransactions.payments.${number}.regularPaymentDetails.status`
      | `orderTransactions.payments.${number}.regularPaymentDetails.savedPaymentMethod`
      | `orderTransactions.payments.${number}.regularPaymentDetails.authorizationDetails.delayedCapture`
      | `orderTransactions.payments.${number}.regularPaymentDetails.platformFee.amount`
      | `orderTransactions.payments.${number}.regularPaymentDetails.platformFee.formattedAmount`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.giftCardPaymentId`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.appId`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.voided`
      | `orderTransactions.payments.${number}.refundDisabled`
      | `orderTransactions.refunds`
      | `orderTransactions.refunds.${number}._id`
      | `orderTransactions.refunds.${number}.details.shippingIncluded`
      | `orderTransactions.refunds.${number}.summary.pending`
      | `failedPaymentIds`
      | `failedPaymentIds.${number}.originalIndex`
      | `failedPaymentIds.${number}.success`
      | `failedPaymentIds.${number}.error.code`
      | `failedPaymentIds.${number}.error.description`,
      7
    > & {
      __applicationErrorsType?: TriggerRefundApplicationErrors;
    }
  >;
}

export function voidAuthorizedPayments(
  httpClient: HttpClient
): VoidAuthorizedPaymentsSignature {
  return (ecomOrderId: string, paymentIds: string[]) =>
    universalVoidAuthorizedPayments(
      ecomOrderId,
      paymentIds,
      // @ts-ignore
      { httpClient }
    );
}

interface VoidAuthorizedPaymentsSignature {
  /**
   * Void authorized payments
   * Transaction statuses for related payments will be updated in async way
   * @param - Wix eCommerce order ID
   * @param - Payment IDs
   */
  (ecomOrderId: string, paymentIds: string[]): Promise<
    NonNullablePaths<
      VoidAuthorizedPaymentsResponse,
      | `orderTransactions.orderId`
      | `orderTransactions.payments`
      | `orderTransactions.payments.${number}.regularPaymentDetails.offlinePayment`
      | `orderTransactions.payments.${number}.regularPaymentDetails.status`
      | `orderTransactions.payments.${number}.regularPaymentDetails.savedPaymentMethod`
      | `orderTransactions.payments.${number}.regularPaymentDetails.authorizationDetails.delayedCapture`
      | `orderTransactions.payments.${number}.regularPaymentDetails.platformFee.amount`
      | `orderTransactions.payments.${number}.regularPaymentDetails.platformFee.formattedAmount`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.giftCardPaymentId`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.appId`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.voided`
      | `orderTransactions.payments.${number}.refundDisabled`
      | `orderTransactions.refunds`
      | `orderTransactions.refunds.${number}._id`
      | `orderTransactions.refunds.${number}.details.shippingIncluded`
      | `orderTransactions.refunds.${number}.summary.pending`,
      7
    > & {
      __applicationErrorsType?: VoidAuthorizedPaymentsApplicationErrors;
    }
  >;
}

export function captureAuthorizedPayments(
  httpClient: HttpClient
): CaptureAuthorizedPaymentsSignature {
  return (
    ecomOrderId: string,
    payments: NonNullablePaths<PaymentCapture, `paymentId`, 2>[]
  ) =>
    universalCaptureAuthorizedPayments(
      ecomOrderId,
      payments,
      // @ts-ignore
      { httpClient }
    );
}

interface CaptureAuthorizedPaymentsSignature {
  /**
   * Capture authorized payments
   * Transaction statuses for related payments will be updated in async way
   * @param - Wix eCommerce order ID
   * @param - Capture payments information
   */
  (
    ecomOrderId: string,
    payments: NonNullablePaths<PaymentCapture, `paymentId`, 2>[]
  ): Promise<
    NonNullablePaths<
      CaptureAuthorizedPaymentsResponse,
      | `orderTransactions.orderId`
      | `orderTransactions.payments`
      | `orderTransactions.payments.${number}.regularPaymentDetails.offlinePayment`
      | `orderTransactions.payments.${number}.regularPaymentDetails.status`
      | `orderTransactions.payments.${number}.regularPaymentDetails.savedPaymentMethod`
      | `orderTransactions.payments.${number}.regularPaymentDetails.authorizationDetails.delayedCapture`
      | `orderTransactions.payments.${number}.regularPaymentDetails.platformFee.amount`
      | `orderTransactions.payments.${number}.regularPaymentDetails.platformFee.formattedAmount`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.giftCardPaymentId`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.appId`
      | `orderTransactions.payments.${number}.giftcardPaymentDetails.voided`
      | `orderTransactions.payments.${number}.refundDisabled`
      | `orderTransactions.refunds`
      | `orderTransactions.refunds.${number}._id`
      | `orderTransactions.refunds.${number}.details.shippingIncluded`
      | `orderTransactions.refunds.${number}.summary.pending`,
      7
    > & {
      __applicationErrorsType?: CaptureAuthorizedPaymentsApplicationErrors;
    }
  >;
}

export function getOrder(httpClient: HttpClient): GetOrderSignature {
  return (_id: string) =>
    universalGetOrder(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetOrderSignature {
  /**
   * Retrieves an order.
   *
   *
   * The `getOrder()` function returns a Promise that resolves when the specified order is retrieved.
   *
   * To retrieve an order's payment and refund details, including amounts, payment methods, and payment statuses, pass the order ID to [`listTransactionsForSingleOrder( )`](https://www.wix.com/velo/reference/wix-ecom-backend/ordertransactions/listtransactionsforsingleorder).
   * @param - ID of the order to retrieve.
   * @returns Fulfilled - The requested order.
   */
  (_id: string): Promise<
    NonNullablePaths<
      Order,
      | `number`
      | `lineItems`
      | `lineItems.${number}._id`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.totalDiscount.amount`
      | `lineItems.${number}.totalDiscount.formattedAmount`
      | `lineItems.${number}.physicalProperties.shippable`
      | `lineItems.${number}.itemType.preset`
      | `lineItems.${number}.itemType.custom`
      | `lineItems.${number}.paymentOption`
      | `lineItems.${number}.taxDetails.taxRate`
      | `lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `lineItems.${number}.digitalFile.fileId`
      | `lineItems.${number}.subscriptionInfo.cycleNumber`
      | `lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `lineItems.${number}.priceDescription.original`
      | `lineItems.${number}.taxableAddress.addressType`
      | `lineItems.${number}.priceUndetermined`
      | `buyerInfo.visitorId`
      | `buyerInfo.memberId`
      | `paymentStatus`
      | `fulfillmentStatus`
      | `weightUnit`
      | `taxIncludedInPrices`
      | `billingInfo.address.streetAddress.number`
      | `billingInfo.address.streetAddress.name`
      | `billingInfo.contactDetails.vatId._id`
      | `billingInfo.contactDetails.vatId.type`
      | `shippingInfo.title`
      | `shippingInfo.logistics.pickupDetails.pickupMethod`
      | `status`
      | `taxInfo.taxBreakdown`
      | `taxInfo.taxBreakdown.${number}.taxName`
      | `taxInfo.taxBreakdown.${number}.taxType`
      | `taxInfo.taxBreakdown.${number}.jurisdiction`
      | `taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `taxInfo.taxBreakdown.${number}.rate`
      | `appliedDiscounts`
      | `appliedDiscounts.${number}.coupon._id`
      | `appliedDiscounts.${number}.coupon.code`
      | `appliedDiscounts.${number}.coupon.name`
      | `appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `appliedDiscounts.${number}.discountRule._id`
      | `appliedDiscounts.${number}.discountRule.name.original`
      | `appliedDiscounts.${number}.discountType`
      | `activities`
      | `activities.${number}.merchantComment.message`
      | `activities.${number}.orderRefunded.manual`
      | `activities.${number}.orderRefunded.reason`
      | `activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `activities.${number}.savedPaymentMethod.name`
      | `activities.${number}.authorizedPaymentCreated.paymentId`
      | `activities.${number}.authorizedPaymentCaptured.paymentId`
      | `activities.${number}.authorizedPaymentVoided.paymentId`
      | `activities.${number}.refundInitiated.refundId`
      | `activities.${number}.paymentRefunded.refundId`
      | `activities.${number}.paymentRefundFailed.refundId`
      | `activities.${number}.paymentPending.paymentId`
      | `activities.${number}.paymentCanceled.paymentId`
      | `activities.${number}.paymentDeclined.paymentId`
      | `activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `activities.${number}.receiptCreated.paymentId`
      | `activities.${number}.receiptSent.paymentId`
      | `activities.${number}.chargebackCreated.paymentId`
      | `activities.${number}.chargebackCreated.chargebackId`
      | `activities.${number}.chargebackReversed.paymentId`
      | `activities.${number}.chargebackReversed.chargebackId`
      | `activities.${number}.type`
      | `activities.${number}.activityType`
      | `attributionSource`
      | `createdBy.userId`
      | `createdBy.memberId`
      | `createdBy.visitorId`
      | `createdBy.appId`
      | `channelInfo.type`
      | `customFields`
      | `customFields.${number}.title`
      | `balanceSummary.balance.amount`
      | `balanceSummary.balance.formattedAmount`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}._id`
      | `additionalFees.${number}.source`
      | `tags.privateTags.tagIds`
      | `businessLocation._id`
      | `businessLocation.name`
      | `platformFeeSummary.fees`
      | `platformFeeSummary.fees.${number}.lineItemId`
      | `platformFeeSummary.fees.${number}.chargeType`
      | `platformFeeSummary.fees.${number}.percentageRate`,
      6
    > & {
      __applicationErrorsType?: GetOrderApplicationErrors;
    }
  >;
}

export function searchOrders(httpClient: HttpClient): SearchOrdersSignature {
  return (search: OrderSearch) =>
    universalSearchOrders(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchOrdersSignature {
  /**
   * Retrieves a list of orders, given the provided paging, filtering, and sorting.
   *
   *
   * Search Orders runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order.
   * - `cursorPaging.limit` is `100`.
   *
   * > **Note:** Search Orders does not return orders with `status: "INITIALIZED"`, regardless of the filter applied. To retrieve an order with `status: "INITIALIZED"`, use [Get Order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/orders/get-order).
   *
   * To learn how to query or search orders, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
   * @param - Search options.
   */
  (search: OrderSearch): Promise<
    NonNullablePaths<
      SearchOrdersResponse,
      | `orders`
      | `orders.${number}.number`
      | `orders.${number}.buyerInfo.visitorId`
      | `orders.${number}.buyerInfo.memberId`
      | `orders.${number}.paymentStatus`
      | `orders.${number}.fulfillmentStatus`
      | `orders.${number}.weightUnit`
      | `orders.${number}.taxIncludedInPrices`
      | `orders.${number}.shippingInfo.title`
      | `orders.${number}.status`
      | `orders.${number}.attributionSource`
      | `orders.${number}.createdBy.userId`
      | `orders.${number}.createdBy.memberId`
      | `orders.${number}.createdBy.visitorId`
      | `orders.${number}.createdBy.appId`
      | `orders.${number}.channelInfo.type`
      | `orders.${number}.balanceSummary.balance.amount`
      | `orders.${number}.balanceSummary.balance.formattedAmount`
      | `orders.${number}.businessLocation._id`
      | `orders.${number}.businessLocation.name`,
      6
    >
  >;
}

export function createOrder(httpClient: HttpClient): CreateOrderSignature {
  return (
    order: NonNullablePaths<
      Order,
      | `channelInfo`
      | `currencyConversionDetails.conversionRate`
      | `currencyConversionDetails.originalCurrency`
      | `lineItems`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.itemType`
      | `lineItems.${number}.price`
      | `lineItems.${number}.productName`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.quantity`
      | `priceSummary`,
      5
    >,
    options?: CreateOrderOptions
  ) =>
    universalCreateOrder(
      order,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateOrderSignature {
  /**
   * Creates an order.
   *
   *
   * The `createOrder()` function returns a Promise that resolves when the order is created.
   *
   * > **Notes:**
   * > + If an item is digital - `lineItems[i].itemType.preset: DIGITAL` - then `lineItems[i].digitalFile` must be provided.
   * > + If `lineItems[i].id` is passed, it must be either a valid GUID, or empty.
   * @param - Order info.
   * @returns Newly created order.
   */
  (
    order: NonNullablePaths<
      Order,
      | `channelInfo`
      | `currencyConversionDetails.conversionRate`
      | `currencyConversionDetails.originalCurrency`
      | `lineItems`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.itemType`
      | `lineItems.${number}.price`
      | `lineItems.${number}.productName`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.quantity`
      | `priceSummary`,
      5
    >,
    options?: CreateOrderOptions
  ): Promise<
    NonNullablePaths<
      Order,
      | `number`
      | `lineItems`
      | `lineItems.${number}._id`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.totalDiscount.amount`
      | `lineItems.${number}.totalDiscount.formattedAmount`
      | `lineItems.${number}.physicalProperties.shippable`
      | `lineItems.${number}.itemType.preset`
      | `lineItems.${number}.itemType.custom`
      | `lineItems.${number}.paymentOption`
      | `lineItems.${number}.taxDetails.taxRate`
      | `lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `lineItems.${number}.digitalFile.fileId`
      | `lineItems.${number}.subscriptionInfo.cycleNumber`
      | `lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `lineItems.${number}.priceDescription.original`
      | `lineItems.${number}.taxableAddress.addressType`
      | `lineItems.${number}.priceUndetermined`
      | `buyerInfo.visitorId`
      | `buyerInfo.memberId`
      | `paymentStatus`
      | `fulfillmentStatus`
      | `weightUnit`
      | `taxIncludedInPrices`
      | `billingInfo.address.streetAddress.number`
      | `billingInfo.address.streetAddress.name`
      | `billingInfo.contactDetails.vatId._id`
      | `billingInfo.contactDetails.vatId.type`
      | `shippingInfo.title`
      | `shippingInfo.logistics.pickupDetails.pickupMethod`
      | `status`
      | `taxInfo.taxBreakdown`
      | `taxInfo.taxBreakdown.${number}.taxName`
      | `taxInfo.taxBreakdown.${number}.taxType`
      | `taxInfo.taxBreakdown.${number}.jurisdiction`
      | `taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `taxInfo.taxBreakdown.${number}.rate`
      | `appliedDiscounts`
      | `appliedDiscounts.${number}.coupon._id`
      | `appliedDiscounts.${number}.coupon.code`
      | `appliedDiscounts.${number}.coupon.name`
      | `appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `appliedDiscounts.${number}.discountRule._id`
      | `appliedDiscounts.${number}.discountRule.name.original`
      | `appliedDiscounts.${number}.discountType`
      | `activities`
      | `activities.${number}.merchantComment.message`
      | `activities.${number}.orderRefunded.manual`
      | `activities.${number}.orderRefunded.reason`
      | `activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `activities.${number}.savedPaymentMethod.name`
      | `activities.${number}.authorizedPaymentCreated.paymentId`
      | `activities.${number}.authorizedPaymentCaptured.paymentId`
      | `activities.${number}.authorizedPaymentVoided.paymentId`
      | `activities.${number}.refundInitiated.refundId`
      | `activities.${number}.paymentRefunded.refundId`
      | `activities.${number}.paymentRefundFailed.refundId`
      | `activities.${number}.paymentPending.paymentId`
      | `activities.${number}.paymentCanceled.paymentId`
      | `activities.${number}.paymentDeclined.paymentId`
      | `activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `activities.${number}.receiptCreated.paymentId`
      | `activities.${number}.receiptSent.paymentId`
      | `activities.${number}.chargebackCreated.paymentId`
      | `activities.${number}.chargebackCreated.chargebackId`
      | `activities.${number}.chargebackReversed.paymentId`
      | `activities.${number}.chargebackReversed.chargebackId`
      | `activities.${number}.type`
      | `activities.${number}.activityType`
      | `attributionSource`
      | `createdBy.userId`
      | `createdBy.memberId`
      | `createdBy.visitorId`
      | `createdBy.appId`
      | `channelInfo.type`
      | `customFields`
      | `customFields.${number}.title`
      | `balanceSummary.balance.amount`
      | `balanceSummary.balance.formattedAmount`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}._id`
      | `additionalFees.${number}.source`
      | `tags.privateTags.tagIds`
      | `businessLocation._id`
      | `businessLocation.name`
      | `platformFeeSummary.fees`
      | `platformFeeSummary.fees.${number}.lineItemId`
      | `platformFeeSummary.fees.${number}.chargeType`
      | `platformFeeSummary.fees.${number}.percentageRate`,
      6
    > & {
      __applicationErrorsType?: CreateOrderApplicationErrors;
    }
  >;
}

export function updateOrder(httpClient: HttpClient): UpdateOrderSignature {
  return (_id: string, order: UpdateOrder) =>
    universalUpdateOrder(
      _id,
      order,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateOrderSignature {
  /**
   * Updates an order.
   *
   *
   * The `updateOrder()` function returns a Promise that resolves when the specified order's information is updated.
   *
   * Currently, the following fields can be updated:
   * + `order.buyerInfo.email`
   * + `order.buyerLanguage`
   * + `order.weightUnit`
   * + `order.billingInfo.address`
   * + `order.billingInfo.contactDetails`
   * + `order.archived`
   * + `order.attributionSource`
   * + `order.seenByAHuman`
   * + `order.recipientInfo.address`
   * + `order.recipientInfo.contactDetails`
   * + `order.shippingInfo.logistics.shippingDestination.address`
   * + `order.shippingInfo.logistics.shippingDestination.contactDetails`
   *
   * To update a field's value, include the new value in the `order` object in the method parameters.
   * To remove a field's value, pass `null`.
   *
   * > **Note:** Removing `buyerInfo` or `contactDetails` fields results in an error.
   *
   * To update an order's payment status, use [`updatePaymentStatus( )`](https://www.wix.com/velo/reference/wix-ecom-backend/ordertransactions/updatepaymentstatus).
   * @param - Order ID.
   * @returns Newly created order.
   */
  (_id: string, order: UpdateOrder): Promise<
    NonNullablePaths<
      Order,
      | `number`
      | `lineItems`
      | `lineItems.${number}._id`
      | `lineItems.${number}.productName.original`
      | `lineItems.${number}.catalogReference.catalogItemId`
      | `lineItems.${number}.catalogReference.appId`
      | `lineItems.${number}.quantity`
      | `lineItems.${number}.totalDiscount.amount`
      | `lineItems.${number}.totalDiscount.formattedAmount`
      | `lineItems.${number}.physicalProperties.shippable`
      | `lineItems.${number}.itemType.preset`
      | `lineItems.${number}.itemType.custom`
      | `lineItems.${number}.paymentOption`
      | `lineItems.${number}.taxDetails.taxRate`
      | `lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `lineItems.${number}.digitalFile.fileId`
      | `lineItems.${number}.subscriptionInfo.cycleNumber`
      | `lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `lineItems.${number}.priceDescription.original`
      | `lineItems.${number}.taxableAddress.addressType`
      | `lineItems.${number}.priceUndetermined`
      | `buyerInfo.visitorId`
      | `buyerInfo.memberId`
      | `paymentStatus`
      | `fulfillmentStatus`
      | `weightUnit`
      | `taxIncludedInPrices`
      | `billingInfo.address.streetAddress.number`
      | `billingInfo.address.streetAddress.name`
      | `billingInfo.contactDetails.vatId._id`
      | `billingInfo.contactDetails.vatId.type`
      | `shippingInfo.title`
      | `shippingInfo.logistics.pickupDetails.pickupMethod`
      | `status`
      | `taxInfo.taxBreakdown`
      | `taxInfo.taxBreakdown.${number}.taxName`
      | `taxInfo.taxBreakdown.${number}.taxType`
      | `taxInfo.taxBreakdown.${number}.jurisdiction`
      | `taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `taxInfo.taxBreakdown.${number}.rate`
      | `appliedDiscounts`
      | `appliedDiscounts.${number}.coupon._id`
      | `appliedDiscounts.${number}.coupon.code`
      | `appliedDiscounts.${number}.coupon.name`
      | `appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `appliedDiscounts.${number}.discountRule._id`
      | `appliedDiscounts.${number}.discountRule.name.original`
      | `appliedDiscounts.${number}.discountType`
      | `activities`
      | `activities.${number}.merchantComment.message`
      | `activities.${number}.orderRefunded.manual`
      | `activities.${number}.orderRefunded.reason`
      | `activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `activities.${number}.savedPaymentMethod.name`
      | `activities.${number}.authorizedPaymentCreated.paymentId`
      | `activities.${number}.authorizedPaymentCaptured.paymentId`
      | `activities.${number}.authorizedPaymentVoided.paymentId`
      | `activities.${number}.refundInitiated.refundId`
      | `activities.${number}.paymentRefunded.refundId`
      | `activities.${number}.paymentRefundFailed.refundId`
      | `activities.${number}.paymentPending.paymentId`
      | `activities.${number}.paymentCanceled.paymentId`
      | `activities.${number}.paymentDeclined.paymentId`
      | `activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `activities.${number}.receiptCreated.paymentId`
      | `activities.${number}.receiptSent.paymentId`
      | `activities.${number}.chargebackCreated.paymentId`
      | `activities.${number}.chargebackCreated.chargebackId`
      | `activities.${number}.chargebackReversed.paymentId`
      | `activities.${number}.chargebackReversed.chargebackId`
      | `activities.${number}.type`
      | `activities.${number}.activityType`
      | `attributionSource`
      | `createdBy.userId`
      | `createdBy.memberId`
      | `createdBy.visitorId`
      | `createdBy.appId`
      | `channelInfo.type`
      | `customFields`
      | `customFields.${number}.title`
      | `balanceSummary.balance.amount`
      | `balanceSummary.balance.formattedAmount`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}._id`
      | `additionalFees.${number}.source`
      | `tags.privateTags.tagIds`
      | `businessLocation._id`
      | `businessLocation.name`
      | `platformFeeSummary.fees`
      | `platformFeeSummary.fees.${number}.lineItemId`
      | `platformFeeSummary.fees.${number}.chargeType`
      | `platformFeeSummary.fees.${number}.percentageRate`,
      6
    > & {
      __applicationErrorsType?: UpdateOrderApplicationErrors;
    }
  >;
}

export function bulkUpdateOrders(
  httpClient: HttpClient
): BulkUpdateOrdersSignature {
  return (
    orders: NonNullablePaths<MaskedOrder, `order` | `order._id`, 3>[],
    options?: BulkUpdateOrdersOptions
  ) =>
    universalBulkUpdateOrders(
      orders,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOrdersSignature {
  /**
   * Updates up to 100 orders.
   *
   *
   * The `bulkUpdateOrders()` function returns a Promise that resolves when the specified orders' information is updated.
   *
   * Currently, the following fields can be updated:
   * + `order.buyerInfo.email`
   * + `order.buyerLanguage`
   * + `order.weightUnit`
   * + `order.billingInfo.address`
   * + `order.billingInfo.contactDetails`
   * + `order.archived`
   * + `order.attributionSource`
   * + `order.seenByAHuman`
   * + `order.recipientInfo.address`
   * + `order.recipientInfo.contactDetails`
   * + `order.shippingInfo.logistics.shippingDestination.address`
   * + `order.shippingInfo.logistics.shippingDestination.contactDetails`
   *
   * To update a field's value, include the new value in the `orders.order` object in the method parameters.
   * To remove a field's value, pass `null`.
   *
   * > **Note:** Removing `buyerInfo` or `contactDetails` fields results in an error.
   *
   * To update an order's payment status, use [`updatePaymentStatus( )`](https://www.wix.com/velo/reference/wix-ecom-backend/ordertransactions/updatepaymentstatus).
   * @param - Orders to update.
   */
  (
    orders: NonNullablePaths<MaskedOrder, `order` | `order._id`, 3>[],
    options?: BulkUpdateOrdersOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateOrdersResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.number`
      | `results.${number}.item.buyerInfo.visitorId`
      | `results.${number}.item.buyerInfo.memberId`
      | `results.${number}.item.paymentStatus`
      | `results.${number}.item.fulfillmentStatus`
      | `results.${number}.item.weightUnit`
      | `results.${number}.item.taxIncludedInPrices`
      | `results.${number}.item.shippingInfo.title`
      | `results.${number}.item.status`
      | `results.${number}.item.attributionSource`
      | `results.${number}.item.createdBy.userId`
      | `results.${number}.item.createdBy.memberId`
      | `results.${number}.item.createdBy.visitorId`
      | `results.${number}.item.createdBy.appId`
      | `results.${number}.item.channelInfo.type`
      | `results.${number}.item.businessLocation._id`
      | `results.${number}.item.businessLocation.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

/** @internal */
export function updateOrderLineItem(
  httpClient: HttpClient
): UpdateOrderLineItemSignature {
  return (
    identifiers: NonNullablePaths<
      UpdateOrderLineItemIdentifiers,
      `_id` | `lineItemId`,
      2
    >,
    lineItem: UpdateOrderLineItem
  ) =>
    universalUpdateOrderLineItem(
      identifiers,
      lineItem,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateOrderLineItemSignature {
  /** @deprecated */
  (
    identifiers: NonNullablePaths<
      UpdateOrderLineItemIdentifiers,
      `_id` | `lineItemId`,
      2
    >,
    lineItem: UpdateOrderLineItem
  ): Promise<
    NonNullablePaths<
      UpdateOrderLineItemResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    >
  >;
}

/** @internal */
export function addActivity(httpClient: HttpClient): AddActivitySignature {
  return (_id: string, activity: PublicActivity) =>
    universalAddActivity(
      _id,
      activity,
      // @ts-ignore
      { httpClient }
    );
}

interface AddActivitySignature {
  /**
   * Adds a merchant comment to an order.
   * > **Note:** Activities with type CustomActivity not supported
   * @param - Order ID.
   * @param - Activity info.
   * @deprecated
   */
  (_id: string, activity: PublicActivity): Promise<
    NonNullablePaths<
      AddActivityResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`
      | `activityId`,
      7
    >
  >;
}

export function addActivities(httpClient: HttpClient): AddActivitiesSignature {
  return (
    orderId: string,
    options?: NonNullablePaths<
      AddActivitiesOptions,
      `orderActivities.${number}.activityType`,
      4
    >
  ) =>
    universalAddActivities(
      orderId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddActivitiesSignature {
  /**
   * Adds up to 300 activities to an order in a single request.
   *
   *
   * An order activity is a record of an action taken on an order. Use this endpoint to add merchant comments or record order-related events.
   * @param - ID of the order to add activities to.
   */
  (
    orderId: string,
    options?: NonNullablePaths<
      AddActivitiesOptions,
      `orderActivities.${number}.activityType`,
      4
    >
  ): Promise<
    NonNullablePaths<
      AddActivitiesResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`
      | `activityIds`,
      7
    >
  >;
}

/** @internal */
export function updateActivity(
  httpClient: HttpClient
): UpdateActivitySignature {
  return (
    identifiers: NonNullablePaths<
      UpdateActivityIdentifiers,
      `_id` | `activityId`,
      2
    >,
    activity: PublicActivity
  ) =>
    universalUpdateActivity(
      identifiers,
      activity,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateActivitySignature {
  /**
   * Updates an order's activity.
   * @param - Activity info.
   */
  (
    identifiers: NonNullablePaths<
      UpdateActivityIdentifiers,
      `_id` | `activityId`,
      2
    >,
    activity: PublicActivity
  ): Promise<
    NonNullablePaths<
      UpdateActivityResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    >
  >;
}

/** @internal */
export function deleteActivity(
  httpClient: HttpClient
): DeleteActivitySignature {
  return (
    identifiers: NonNullablePaths<
      DeleteActivityIdentifiers,
      `_id` | `activityId`,
      2
    >
  ) =>
    universalDeleteActivity(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteActivitySignature {
  /**
   * Delete's an order's activity.
   *
   * Only custom activities and merchant comments can be deleted.
   */
  (
    identifiers: NonNullablePaths<
      DeleteActivityIdentifiers,
      `_id` | `activityId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      DeleteActivityResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    >
  >;
}

export function cancelOrder(httpClient: HttpClient): CancelOrderSignature {
  return (_id: string, options?: CancelOrderOptions) =>
    universalCancelOrder(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelOrderSignature {
  /**
   * Cancels an order.
   *
   *
   * The `cancelOrder()` function returns a Promise that resolves when the specified order is canceled and the `order.status` field changes to `CANCELED`.
   * @param - Order ID.
   */
  (_id: string, options?: CancelOrderOptions): Promise<
    NonNullablePaths<
      CancelOrderResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    > & {
      __applicationErrorsType?: CancelOrderApplicationErrors;
    }
  >;
}

/** @internal */
export function updateOrderStatus(
  httpClient: HttpClient
): UpdateOrderStatusSignature {
  return (orderId: string, status: OrderStatusWithLiterals) =>
    universalUpdateOrderStatus(
      orderId,
      status,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateOrderStatusSignature {
  /**
   * Updates order status.
   *
   * >CANCELED and REJECTED are final statuses. Update from either of those will fail.
   * >Updates into INITIALIZED are not supported.
   *
   * Approving order:
   * APPROVED status can be set while order has one of [INITIALIZED, PENDING] statuses.
   *
   * Approving an order which was created with CreateOrderRequest.settings.order_approval_strategy = PAYMENT_METHOD_SAVED
   * will cause an error.
   *
   * Setting PENDING status:
   * Only orders in INITIALIZED status can be updated into PENDING.
   * @param - Order ID.
   * @param - New order status.
   */
  (orderId: string, status: OrderStatusWithLiterals): Promise<
    NonNullablePaths<
      UpdateOrderStatusResponse,
      | `order.number`
      | `order.lineItems`
      | `order.lineItems.${number}._id`
      | `order.lineItems.${number}.productName.original`
      | `order.lineItems.${number}.catalogReference.catalogItemId`
      | `order.lineItems.${number}.catalogReference.appId`
      | `order.lineItems.${number}.quantity`
      | `order.lineItems.${number}.totalDiscount.amount`
      | `order.lineItems.${number}.totalDiscount.formattedAmount`
      | `order.lineItems.${number}.physicalProperties.shippable`
      | `order.lineItems.${number}.itemType.preset`
      | `order.lineItems.${number}.itemType.custom`
      | `order.lineItems.${number}.paymentOption`
      | `order.lineItems.${number}.taxDetails.taxRate`
      | `order.lineItems.${number}.taxInfo.taxIncludedInPrice`
      | `order.lineItems.${number}.digitalFile.fileId`
      | `order.lineItems.${number}.subscriptionInfo.cycleNumber`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionOptionTitle`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.frequency`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.autoRenewal`
      | `order.lineItems.${number}.subscriptionInfo.subscriptionSettings.enableCustomerCancellation`
      | `order.lineItems.${number}.priceDescription.original`
      | `order.lineItems.${number}.taxableAddress.addressType`
      | `order.lineItems.${number}.priceUndetermined`
      | `order.buyerInfo.visitorId`
      | `order.buyerInfo.memberId`
      | `order.paymentStatus`
      | `order.fulfillmentStatus`
      | `order.weightUnit`
      | `order.taxIncludedInPrices`
      | `order.billingInfo.address.streetAddress.number`
      | `order.billingInfo.address.streetAddress.name`
      | `order.billingInfo.contactDetails.vatId._id`
      | `order.billingInfo.contactDetails.vatId.type`
      | `order.shippingInfo.title`
      | `order.shippingInfo.logistics.pickupDetails.pickupMethod`
      | `order.status`
      | `order.taxInfo.taxBreakdown`
      | `order.taxInfo.taxBreakdown.${number}.taxName`
      | `order.taxInfo.taxBreakdown.${number}.taxType`
      | `order.taxInfo.taxBreakdown.${number}.jurisdiction`
      | `order.taxInfo.taxBreakdown.${number}.jurisdictionType`
      | `order.taxInfo.taxBreakdown.${number}.rate`
      | `order.appliedDiscounts`
      | `order.appliedDiscounts.${number}.coupon._id`
      | `order.appliedDiscounts.${number}.coupon.code`
      | `order.appliedDiscounts.${number}.coupon.name`
      | `order.appliedDiscounts.${number}.merchantDiscount.discountReason`
      | `order.appliedDiscounts.${number}.discountRule._id`
      | `order.appliedDiscounts.${number}.discountRule.name.original`
      | `order.appliedDiscounts.${number}.discountType`
      | `order.activities`
      | `order.activities.${number}.merchantComment.message`
      | `order.activities.${number}.orderRefunded.manual`
      | `order.activities.${number}.orderRefunded.reason`
      | `order.activities.${number}.draftOrderChangesApplied.draftOrderId`
      | `order.activities.${number}.savedPaymentMethod.name`
      | `order.activities.${number}.authorizedPaymentCreated.paymentId`
      | `order.activities.${number}.authorizedPaymentCaptured.paymentId`
      | `order.activities.${number}.authorizedPaymentVoided.paymentId`
      | `order.activities.${number}.refundInitiated.refundId`
      | `order.activities.${number}.paymentRefunded.refundId`
      | `order.activities.${number}.paymentRefundFailed.refundId`
      | `order.activities.${number}.paymentPending.paymentId`
      | `order.activities.${number}.paymentCanceled.paymentId`
      | `order.activities.${number}.paymentDeclined.paymentId`
      | `order.activities.${number}.receiptCreated.wixReceipt.receiptId`
      | `order.activities.${number}.receiptCreated.paymentId`
      | `order.activities.${number}.receiptSent.paymentId`
      | `order.activities.${number}.chargebackCreated.paymentId`
      | `order.activities.${number}.chargebackCreated.chargebackId`
      | `order.activities.${number}.chargebackReversed.paymentId`
      | `order.activities.${number}.chargebackReversed.chargebackId`
      | `order.activities.${number}.type`
      | `order.activities.${number}.activityType`
      | `order.attributionSource`
      | `order.createdBy.userId`
      | `order.createdBy.memberId`
      | `order.createdBy.visitorId`
      | `order.createdBy.appId`
      | `order.channelInfo.type`
      | `order.customFields`
      | `order.customFields.${number}.title`
      | `order.balanceSummary.balance.amount`
      | `order.balanceSummary.balance.formattedAmount`
      | `order.additionalFees`
      | `order.additionalFees.${number}.name`
      | `order.additionalFees.${number}._id`
      | `order.additionalFees.${number}.source`
      | `order.tags.privateTags.tagIds`
      | `order.businessLocation._id`
      | `order.businessLocation.name`
      | `order.platformFeeSummary.fees`
      | `order.platformFeeSummary.fees.${number}.lineItemId`
      | `order.platformFeeSummary.fees.${number}.chargeType`
      | `order.platformFeeSummary.fees.${number}.percentageRate`,
      7
    > & {
      __applicationErrorsType?: UpdateOrderStatusApplicationErrors;
    }
  >;
}

/** @internal */
export function aggregateOrders(
  httpClient: HttpClient
): AggregateOrdersSignature {
  return (
    options?: NonNullablePaths<AggregateOrdersOptions, `aggregation`, 2>
  ) =>
    universalAggregateOrders(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AggregateOrdersSignature {
  /** */
  (
    options?: NonNullablePaths<AggregateOrdersOptions, `aggregation`, 2>
  ): Promise<AggregateOrdersResponse>;
}

export function bulkUpdateOrderTags(
  httpClient: HttpClient
): BulkUpdateOrderTagsSignature {
  return (orderIds: string[], options?: BulkUpdateOrderTagsOptions) =>
    universalBulkUpdateOrderTags(
      orderIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateOrderTagsSignature {
  /**
   * Synchronously update tags on multiple orders, by list of order IDs.
   *
   * > **Note:** If the same tag is passed to both the `assignTags` and `unassignTags` fields, it will be assigned.
   * @param - IDs of orders to update tags for.
   */
  (orderIds: string[], options?: BulkUpdateOrderTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateOrderTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onOrderApproved = EventDefinition(
  'wix.ecom.v1.order_approved',
  true,
  (event: OrderApprovedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.purchasedDate' },
            { path: 'data.order.lineItems.digitalFile.expirationDate' },
            {
              path: 'data.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.order.shippingInfo.logistics.deliverByDate' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.order.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.order.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.order.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.order.billingInfo.address' },
            {
              path: 'data.order.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'data.order.shippingInfo.logistics.pickupDetails.address' },
            { path: 'data.order.recipientInfo.address' },
          ],
        },
      ])
    )
)<OrderApprovedEnvelope>();
export const onOrderCanceled = EventDefinition(
  'wix.ecom.v1.order_canceled',
  true,
  (event: OrderCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.purchasedDate' },
            { path: 'data.order.lineItems.digitalFile.expirationDate' },
            {
              path: 'data.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.order.shippingInfo.logistics.deliverByDate' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.order.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.order.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.order.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.order.billingInfo.address' },
            {
              path: 'data.order.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'data.order.shippingInfo.logistics.pickupDetails.address' },
            { path: 'data.order.recipientInfo.address' },
          ],
        },
      ])
    )
)<OrderCanceledEnvelope>();
export const onOrderCommitted = EventDefinition(
  'wix.ecom.v1.order_committed',
  true,
  (event: OrderCommittedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.commitDate' },
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.purchasedDate' },
            { path: 'data.order.lineItems.digitalFile.expirationDate' },
            {
              path: 'data.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.order.shippingInfo.logistics.deliverByDate' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.order.activities.createdDate' },
            {
              path: 'data.changes.changedShippingInfo.logistics.deliverByDate',
            },
            {
              path: 'data.changes.changedShippingInfo.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'data.changes.changedShippingInfo.logistics.deliveryTimeSlot.to',
            },
            {
              path: 'data.changes.lineItems.lineItemRemoved.digitalFile.expirationDate',
            },
            {
              path: 'data.changes.lineItems.lineItemRemoved.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'data.order.lineItems.image' },
            { path: 'data.changes.lineItems.lineItemRemoved.image' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.order.lineItems.physicalProperties.weight' },
            {
              path: 'data.changes.lineItems.lineItemRemoved.physicalProperties.weight',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.order.billingInfo.address' },
            {
              path: 'data.order.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'data.order.shippingInfo.logistics.pickupDetails.address' },
            { path: 'data.order.recipientInfo.address' },
            {
              path: 'data.changes.changedShippingInfo.logistics.shippingDestination.address',
            },
            {
              path: 'data.changes.changedShippingInfo.logistics.pickupDetails.address',
            },
          ],
        },
      ])
    )
)<OrderCommittedEnvelope>();
export const onOrderCreated = EventDefinition(
  'wix.ecom.v1.order_created',
  true,
  (event: OrderCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.purchasedDate' },
            { path: 'entity.lineItems.digitalFile.expirationDate' },
            {
              path: 'entity.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.shippingInfo.logistics.deliverByDate' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'entity.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.billingInfo.address' },
            {
              path: 'entity.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'entity.shippingInfo.logistics.pickupDetails.address' },
            { path: 'entity.recipientInfo.address' },
          ],
        },
      ])
    )
)<OrderCreatedEnvelope>();
export const onOrderFulfilled = EventDefinition(
  'wix.ecom.v1.order_fulfilled',
  true,
  (event: OrderFulfilledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.purchasedDate' },
            { path: 'data.order.lineItems.digitalFile.expirationDate' },
            {
              path: 'data.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.order.shippingInfo.logistics.deliverByDate' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.order.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.order.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.order.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.order.billingInfo.address' },
            {
              path: 'data.order.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'data.order.shippingInfo.logistics.pickupDetails.address' },
            { path: 'data.order.recipientInfo.address' },
          ],
        },
      ])
    )
)<OrderFulfilledEnvelope>();
export const onOrderPaymentStatusUpdated = EventDefinition(
  'wix.ecom.v1.order_payment_status_updated',
  true,
  (event: OrderPaymentStatusUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.order.createdDate' },
            { path: 'data.order.updatedDate' },
            { path: 'data.order.purchasedDate' },
            { path: 'data.order.lineItems.digitalFile.expirationDate' },
            {
              path: 'data.order.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.order.shippingInfo.logistics.deliverByDate' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'data.order.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.order.activities.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.order.lineItems.image' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [{ path: 'data.order.lineItems.physicalProperties.weight' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.order.billingInfo.address' },
            {
              path: 'data.order.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'data.order.shippingInfo.logistics.pickupDetails.address' },
            { path: 'data.order.recipientInfo.address' },
          ],
        },
      ])
    )
)<OrderPaymentStatusUpdatedEnvelope>();
export const onOrderUpdated = EventDefinition(
  'wix.ecom.v1.order_updated',
  true,
  (event: OrderUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.purchasedDate' },
            { path: 'entity.lineItems.digitalFile.expirationDate' },
            {
              path: 'entity.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.shippingInfo.logistics.deliverByDate' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shippingInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.activities.createdDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.purchasedDate' },
            { path: 'modifiedFields.lineItems.digitalFile.expirationDate' },
            {
              path: 'modifiedFields.lineItems.subscriptionInfo.subscriptionSettings.startDate',
            },
            { path: 'modifiedFields.shippingInfo.logistics.deliverByDate' },
            {
              path: 'modifiedFields.shippingInfo.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'modifiedFields.shippingInfo.logistics.deliveryTimeSlot.to',
            },
            { path: 'modifiedFields.activities.createdDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.lineItems.image' },
            { path: 'modifiedFields.lineItems.image' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.lineItems.physicalProperties.weight' },
            { path: 'modifiedFields.lineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.billingInfo.address' },
            {
              path: 'entity.shippingInfo.logistics.shippingDestination.address',
            },
            { path: 'entity.shippingInfo.logistics.pickupDetails.address' },
            { path: 'entity.recipientInfo.address' },
            { path: 'modifiedFields.billingInfo.address' },
            {
              path: 'modifiedFields.shippingInfo.logistics.shippingDestination.address',
            },
            {
              path: 'modifiedFields.shippingInfo.logistics.pickupDetails.address',
            },
            { path: 'modifiedFields.recipientInfo.address' },
          ],
        },
      ])
    )
)<OrderUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActionType,
  Activity,
  ActivityContentOneOf,
  ActivityType,
  AddActivitiesOptions,
  AddActivitiesRequest,
  AddActivitiesResponse,
  AddActivityRequest,
  AddActivityResponse,
  AddInternalActivityRequest,
  AddInternalActivityResponse,
  AddToAutomationMigrationPopulationRequest,
  AddToAutomationMigrationPopulationResponse,
  AdditionalFee,
  AdditionalFeeDelta,
  AdditionalFeeDeltaDeltaOneOf,
  AdditionalFeeRefund,
  AdditionalFeeSource,
  Address,
  AddressDescription,
  AddressLocation,
  AddressWithContact,
  AdjustmentType,
  AggregateOrdersOptions,
  AggregateOrdersRequest,
  AggregateOrdersResponse,
  AggregatedRefundSummary,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountDelta,
  AppliedDiscountDeltaDeltaOneOf,
  AppliedDiscountDiscountSourceOneOf,
  ArchiveOrderRequest,
  ArchiveOrderResponse,
  Asset,
  AttributionSource,
  AuthorizationActionFailureDetails,
  AuthorizationCapture,
  AuthorizationCaptureStatus,
  AuthorizationDetails,
  AuthorizationVoid,
  AuthorizationVoidStatus,
  AuthorizedPaymentCaptured,
  AuthorizedPaymentCreated,
  AuthorizedPaymentVoided,
  Balance,
  BalanceSummary,
  BaseEventMetadata,
  BigDecimalWrapper,
  BillingAdjustment,
  BillingAdjustmentPriceSummary,
  BillingSettings,
  BulkActionMetadata,
  BulkArchiveOrdersByFilterRequest,
  BulkArchiveOrdersByFilterResponse,
  BulkArchiveOrdersRequest,
  BulkArchiveOrdersResponse,
  BulkDeleteImportedOrdersRequest,
  BulkDeleteImportedOrdersResponse,
  BulkMarkAsFulfilledByFilterRequest,
  BulkMarkAsFulfilledByFilterResponse,
  BulkMarkAsFulfilledRequest,
  BulkMarkAsFulfilledResponse,
  BulkMarkAsUnfulfilledByFilterRequest,
  BulkMarkAsUnfulfilledByFilterResponse,
  BulkMarkAsUnfulfilledRequest,
  BulkMarkAsUnfulfilledResponse,
  BulkMarkOrdersAsPaidRequest,
  BulkMarkOrdersAsPaidResponse,
  BulkOrderResult,
  BulkSendBuyerPickupConfirmationEmailsRequest,
  BulkSendBuyerPickupConfirmationEmailsResponse,
  BulkSendBuyerShippingConfirmationEmailsRequest,
  BulkSendBuyerShippingConfirmationEmailsResponse,
  BulkSetBusinessLocationRequest,
  BulkSetBusinessLocationResponse,
  BulkSetBusinessLocationResult,
  BulkUnArchiveOrdersByFilterRequest,
  BulkUnArchiveOrdersByFilterResponse,
  BulkUnArchiveOrdersRequest,
  BulkUnArchiveOrdersResponse,
  BulkUpdateOrderTagsOptions,
  BulkUpdateOrderTagsRequest,
  BulkUpdateOrderTagsResponse,
  BulkUpdateOrderTagsResult,
  BulkUpdateOrdersOptions,
  BulkUpdateOrdersRequest,
  BulkUpdateOrdersResponse,
  BuyerInfo,
  BuyerInfoIdOneOf,
  BuyerInfoUpdate,
  CalculateRefundItemRequest,
  CalculateRefundItemResponse,
  CalculateRefundRequest,
  CalculateRefundResponse,
  CalculatedTax,
  CalculatedTaxes,
  Cancel,
  CancelOrderOptions,
  CancelOrderRequest,
  CancelOrderResponse,
  CaptureAuthorizedPaymentsRequest,
  CaptureAuthorizedPaymentsResponse,
  CashRounding,
  CashRoundingDetails,
  CatalogReference,
  ChannelInfo,
  ChannelType,
  ChargeMembershipsOptions,
  ChargeMembershipsRequest,
  ChargeMembershipsResponse,
  ChargeSavedPaymentMethodRequest,
  ChargeSavedPaymentMethodResponse,
  ChargeType,
  Chargeback,
  ChargebackCreated,
  ChargebackReversed,
  ChargebackStatus,
  ChargedBy,
  Color,
  CommitDeltasRequest,
  CommitDeltasResponse,
  CommittedDiffs,
  CommittedDiffsShippingUpdateInfoOneOf,
  CommonAddress,
  CommonAddressStreetOneOf,
  Company,
  Complete,
  Coupon,
  CreateOrderOptions,
  CreateOrderRequest,
  CreateOrderResponse,
  CreatePaymentGatewayOrderRequest,
  CreatePaymentGatewayOrderResponse,
  CreatedBy,
  CreatedByStringOneOf,
  CreditCardDetails,
  CreditCardPaymentMethodDetails,
  CurrencyConversionDetails,
  CursorPaging,
  CursorPagingMetadata,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomActivity,
  CustomAllowedActions,
  CustomField,
  CustomFieldGroup,
  CustomFieldValue,
  Customer,
  DecrementData,
  DecrementItemsQuantityRequest,
  DecrementItemsQuantityResponse,
  DelayedCaptureSettings,
  DeleteActivityIdentifiers,
  DeleteActivityRequest,
  DeleteActivityResponse,
  DeleteByFilterOperation,
  DeleteByIdsOperation,
  DeleteContext,
  DeleteStatus,
  DeliveryLogistics,
  DeliveryLogisticsAddressOneOf,
  DeliveryTimeSlot,
  DeltaPaymentOptionType,
  Deposit,
  DepositType,
  DescriptionLine,
  DescriptionLineDescriptionLineValueOneOf,
  DescriptionLineName,
  DescriptionLineType,
  DescriptionLineValueOneOf,
  DiffmatokyPayload,
  DigitalFile,
  Dimensions,
  DimensionsUnit,
  Discount,
  DiscountOneDiscountTypeOneOf,
  DiscountReason,
  DiscountRule,
  DiscountRuleName,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  DownloadLinkSent,
  DraftOrderChangesApplied,
  DraftOrderCommitSettings,
  DraftOrderDiffs,
  DraftOrderDiffsBillingUpdateInfoOneOf,
  DraftOrderDiffsBuyerUpdateInfoOneOf,
  DraftOrderDiffsRecipientUpdateInfoOneOf,
  DraftOrderDiffsShippingUpdateInfoOneOf,
  Duration,
  DurationUnit,
  EditorlessAssigned,
  EditorlessUnassigned,
  Email,
  EmailEdited,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ErrorInformation,
  EventMetadata,
  ExtendedFields,
  ExternalReceipt,
  ExternalReceiptInfo,
  FocalPoint,
  FormIdentifier,
  FormInfo,
  FreeTrialPeriod,
  FulfillerEmailSent,
  FulfillmentStatus,
  FulfillmentStatusUpdated,
  FulfillmentStatusesAggregate,
  FullAddressContactDetails,
  GetOrderRequest,
  GetOrderResponse,
  GetPaymentCollectabilityStatusRequest,
  GetPaymentCollectabilityStatusResponse,
  GetRefundabilityStatusRequest,
  GetRefundabilityStatusResponse,
  GetShipmentsRequest,
  GetShipmentsResponse,
  GiftCardPaymentDetails,
  GiftCardPaymentRefund,
  HasCustomEmailConfigurationsRequest,
  HasCustomEmailConfigurationsResponse,
  IdAndVersion,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImportOrderRequest,
  ImportOrderResponse,
  ImportedOrderDeleted,
  InternalActivity,
  InternalActivityContentOneOf,
  InternalDocument,
  InternalDocumentUpdateByFilterOperation,
  InternalDocumentUpdateOperation,
  InternalQueryOrdersRequest,
  InternalQueryOrdersResponse,
  InternalUpdateExistingOperation,
  InventoryAction,
  InventoryUpdateDetails,
  InvoiceAdded,
  InvoiceDates,
  InvoiceDynamicPriceTotals,
  InvoiceFields,
  InvoiceSent,
  InvoiceSentEvent,
  InvoiceStatus,
  InvoicesPayment,
  IsInAutomationMigrationPopulationRequest,
  IsInAutomationMigrationPopulationResponse,
  ItemChangedDetails,
  ItemCombination,
  ItemCombinationLineItem,
  ItemMetadata,
  ItemModifier,
  ItemTaxFullDetails,
  ItemType,
  ItemTypeItemTypeDataOneOf,
  ItemTypePreset,
  ItemizedFee,
  JurisdictionType,
  LineItem,
  LineItemAmount,
  LineItemChanges,
  LineItemDelta,
  LineItemDeltaDeltaOneOf,
  LineItemDescriptionLineChange,
  LineItemDiscount,
  LineItemExchangeData,
  LineItemMetaData,
  LineItemModifiersChange,
  LineItemPriceChange,
  LineItemProductNameChange,
  LineItemQuantityChange,
  LineItemQuantityChangeType,
  LineItemRefund,
  LineItemRefundSummary,
  LineItemTax,
  LineItemTaxBreakdown,
  LineItemTaxInfo,
  LineItemUpdate,
  LineItems,
  Locale,
  Location,
  LocationAndQuantity,
  ManagedAdditionalFee,
  ManagedDiscount,
  ManagedLineItem,
  ManuallyRefundableReason,
  MarkAsFulfilledRequest,
  MarkAsFulfilledResponse,
  MarkAsUnfulfilledRequest,
  MarkAsUnfulfilledResponse,
  MarkOrderAsPaidRequest,
  MarkOrderAsPaidResponse,
  MarkOrderAsSeenByHumanRequest,
  MarkOrderAsSeenByHumanResponse,
  MaskedOrder,
  MaskedOrderLineItem,
  MembershipChargeItem,
  MembershipName,
  MembershipPaymentDetails,
  MembershipPaymentRefund,
  MembershipPaymentStatus,
  MerchantComment,
  MerchantDiscount,
  MerchantDiscountMerchantDiscountReasonOneOf,
  MessageEnvelope,
  MetaData,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  ModifierGroup,
  Namespace,
  NamespaceChanged,
  NewExchangeOrderCreated,
  NonRefundableReason,
  OdeditorAssigned,
  OdeditorUnassigned,
  Order,
  OrderActionType,
  OrderActivityTypeEnumActivityType,
  OrderApprovalStrategy,
  OrderApproved,
  OrderApprovedEnvelope,
  OrderCanceled,
  OrderCanceledEnvelope,
  OrderCanceledEventOrderCanceled,
  OrderChange,
  OrderChangeValueOneOf,
  OrderCommittedEnvelope,
  OrderCreateNotifications,
  OrderCreatedEnvelope,
  OrderCreatedFromExchange,
  OrderCreationSettings,
  OrderCreationSettingsEditableByOneOf,
  OrderDeltasCommitted,
  OrderFulfilled,
  OrderFulfilledEnvelope,
  OrderImported,
  OrderItemsRestocked,
  OrderLineItem,
  OrderLineItemChangedDetails,
  OrderNotFulfilled,
  OrderPaid,
  OrderPartiallyPaid,
  OrderPaymentStatusUpdatedEnvelope,
  OrderPending,
  OrderPlaced,
  OrderRefunded,
  OrderRejected,
  OrderRejectedEventOrderRejected,
  OrderSearchSpec,
  OrderSettings,
  OrderSettingsAllowedActionsOneOf,
  OrderSettingsEditableByOneOf,
  OrderStatus,
  OrderTaxBreakdown,
  OrderTaxInfo,
  OrderTransactions,
  OrderUpdatedEnvelope,
  OrdersExperiments,
  Origin,
  OwnerApps,
  Payment,
  PaymentCanceled,
  PaymentCanceledPaymentDetailsOneOf,
  PaymentCapture,
  PaymentCollectabilityStatus,
  PaymentCollectionCreatePaymentGatewayOrderOptions,
  PaymentCollectionMarkOrderAsPaidOptions,
  PaymentDeclined,
  PaymentDeclinedPaymentDetailsOneOf,
  PaymentMethodName,
  PaymentOptionType,
  PaymentPaymentDetailsOneOf,
  PaymentPending,
  PaymentPendingPaymentDetailsOneOf,
  PaymentReceiptInfoOneOf,
  PaymentRefund,
  PaymentRefundFailed,
  PaymentRefunded,
  PaymentStatus,
  PaymentStatusEnumPaymentStatus,
  PaymentStatusUpdated,
  Payments,
  Phone,
  PhysicalProperties,
  PicassoAssigned,
  PicassoUnassigned,
  PickupAddress,
  PickupDetails,
  PickupMethod,
  PickupReadyEmailSent,
  Placement,
  PlainTextValue,
  PlatformFee,
  PlatformFeeSummary,
  PlatformPaging,
  PlatformPagingMetadata,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  PredefinedPaymentMethod,
  PreparePaymentCollectionOptions,
  PreparePaymentCollectionRequest,
  PreparePaymentCollectionResponse,
  PreviewBuyerConfirmationEmailRequest,
  PreviewBuyerConfirmationEmailResponse,
  PreviewBuyerPaymentsReceivedEmailRequest,
  PreviewBuyerPaymentsReceivedEmailResponse,
  PreviewBuyerPickupConfirmationEmailRequest,
  PreviewBuyerPickupConfirmationEmailResponse,
  PreviewCancelEmailRequest,
  PreviewCancelEmailResponse,
  PreviewCancelRefundEmailRequest,
  PreviewCancelRefundEmailResponse,
  PreviewEmailByTypeRequest,
  PreviewEmailByTypeResponse,
  PreviewEmailType,
  PreviewRefundEmailRequest,
  PreviewRefundEmailResponse,
  PreviewResendDownloadLinksEmailRequest,
  PreviewResendDownloadLinksEmailResponse,
  PreviewShippingConfirmationEmailRequest,
  PreviewShippingConfirmationEmailResponse,
  Price,
  PriceDescription,
  PriceSummary,
  ProductName,
  PublicActivity,
  PublicActivityContentOneOf,
  QueryOrderRequest,
  QueryOrderResponse,
  QuotesAddress,
  Reason,
  ReceiptCreated,
  ReceiptCreatedReceiptInfoOneOf,
  ReceiptSent,
  ReceiptSentReceiptInfoOneOf,
  RecordManuallyCollectedPaymentOptions,
  RecordManuallyCollectedPaymentRequest,
  RecordManuallyCollectedPaymentResponse,
  RedirectUrls,
  Refund,
  RefundDetails,
  RefundInitiated,
  RefundItem,
  RefundItemsBreakdown,
  RefundSideEffects,
  RefundStatus,
  RefundStatusInfo,
  RefundTransaction,
  Refundability,
  RefundabilityAdditionalRefundabilityInfoOneOf,
  RefundableStatus,
  RefundedAsStoreCredit,
  RefundedPayment,
  RefundedPaymentKindOneOf,
  RegularPayment,
  RegularPaymentDetails,
  RegularPaymentDetailsPaymentMethodDetailsOneOf,
  RegularPaymentPaymentMethodDetailsOneOf,
  RegularPaymentRefund,
  Reschedule,
  RestockInfo,
  RestockItem,
  RestockLocation,
  RestockType,
  RestoreInfo,
  SavedPaymentMethod,
  ScheduledAction,
  SearchOrdersRequest,
  SearchOrdersResponse,
  SendBuyerConfirmationEmailRequest,
  SendBuyerConfirmationEmailResponse,
  SendBuyerPaymentsReceivedEmailRequest,
  SendBuyerPaymentsReceivedEmailResponse,
  SendBuyerPickupConfirmationEmailRequest,
  SendBuyerPickupConfirmationEmailResponse,
  SendBuyerShippingConfirmationEmailRequest,
  SendBuyerShippingConfirmationEmailResponse,
  SendCancelRefundEmailRequest,
  SendCancelRefundEmailResponse,
  SendFulfillmentEmailRequest,
  SendFulfillmentEmailResponse,
  SendMerchantOrderReceivedNotificationRequest,
  SendMerchantOrderReceivedNotificationResponse,
  SendMerchantOrderReceivedPushRequest,
  SendMerchantOrderReceivedPushResponse,
  SendOrderUpdatedDomainEventRequest,
  SendOrderUpdatedDomainEventResponse,
  SendRefundEmailRequest,
  SendRefundEmailResponse,
  ServiceProperties,
  ServiceProvisioned,
  ServiceRemoved,
  SetOrderNumberCounterOptions,
  SetOrderNumberCounterRequest,
  SetOrderNumberCounterResponse,
  ShippingAddressEdited,
  ShippingConfirmationEmailSent,
  ShippingInformation,
  ShippingInformationChange,
  ShippingPrice,
  ShippingRefund,
  ShippingRegion,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  Source,
  SourceType,
  SpecificItemsCouponBehavior,
  StandardDetails,
  State,
  StreetAddress,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  Subdivision,
  SubdivisionType,
  SubscriptionDescription,
  SubscriptionFrequency,
  SubscriptionInfo,
  SubscriptionSettings,
  SubscriptionTitle,
  TagList,
  Tags,
  TagsTagList,
  TagsTags,
  Task,
  TaskAction,
  TaskActionActionOneOf,
  TaskKey,
  TaxSummary,
  TaxableAddress,
  TaxableAddressTaxableAddressDataOneOf,
  TaxableAddressType,
  TotalPrice,
  TotalPriceChange,
  TrackingLinkAdded,
  TrackingNumberAdded,
  TrackingNumberEdited,
  TransactionStatus,
  TranslatableString,
  TranslatedValue,
  TriggerRefundOptions,
  TriggerRefundRequest,
  TriggerRefundResponse,
  TriggerReindexOrderRequest,
  TriggerReindexRequest,
  TriggerReindexResponse,
  UnArchiveOrderRequest,
  UnArchiveOrderResponse,
  UpdateActivityIdentifiers,
  UpdateActivityRequest,
  UpdateActivityResponse,
  UpdateBillingContactDetailsRequest,
  UpdateBillingContactDetailsResponse,
  UpdateBuyerEmailRequest,
  UpdateBuyerEmailResponse,
  UpdateBuyerInfoRequest,
  UpdateBuyerInfoResponse,
  UpdateInternalDocumentsEvent,
  UpdateInternalDocumentsEventOperationOneOf,
  UpdateLineItemsDescriptionLinesRequest,
  UpdateLineItemsDescriptionLinesResponse,
  UpdateOrder,
  UpdateOrderLineItem,
  UpdateOrderLineItemIdentifiers,
  UpdateOrderLineItemRequest,
  UpdateOrderLineItemResponse,
  UpdateOrderLineItemsRequest,
  UpdateOrderLineItemsResponse,
  UpdateOrderRequest,
  UpdateOrderResponse,
  UpdateOrderShippingAddressRequest,
  UpdateOrderShippingAddressResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  UserDefinedPaymentMethodName,
  UserDefinedPaymentMethodNameKindOneOf,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  V1BulkMarkOrdersAsPaidRequest,
  V1BulkMarkOrdersAsPaidResponse,
  V1CreatePaymentGatewayOrderRequest,
  V1CreatePaymentGatewayOrderResponse,
  V1LineItemDelta,
  V1LineItemDeltaDeltaOneOf,
  V1MarkOrderAsPaidRequest,
  V1MarkOrderAsPaidResponse,
  V1RestockItem,
  V1ScheduledAction,
  V1ShippingInformation,
  Value,
  ValueType,
  VatId,
  VatType,
  VersionedDeleteByIdsOperation,
  VersionedDocumentId,
  VersionedDocumentUpdateOperation,
  VersioningMode,
  VoidAuthorizedPaymentsRequest,
  VoidAuthorizedPaymentsResponse,
  WebhookIdentityType,
  WeightUnit,
  WixReceipt,
  WixReceiptInfo,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './ecom-v1-order-orders.universal.js';
